import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { clinicAPI, authAPI } from '../services/api'
import './DashboardPage.css'

function DashboardPage() {
  const [clinicId, setClinicId] = useState(null)
  const [pendingPatients, setPendingPatients] = useState([])
  const [claimedPatients, setClaimedPatients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('pending')
  const navigate = useNavigate()
  
  useEffect(() => {
    // Check authentication
    authAPI.checkAuth().then(isAuth => {
      if (!isAuth) {
        navigate('/login')
        return
      }
      
      // TODO: Get clinic_id from user profile or select clinic
      // For now, get from localStorage or prompt user to select
      const savedClinicId = localStorage.getItem('clinic_id')
      if (savedClinicId) {
        const clinicIdNum = parseInt(savedClinicId)
        setClinicId(clinicIdNum)
        loadPatients(clinicIdNum)
      } else {
        // TODO: Show clinic selection dialog
        // For now, show error
        setError('Brak wybranej przychodni. Skontaktuj się z administratorem.')
        setLoading(false)
      }
    }).catch(err => {
      console.error('Auth check error:', err)
      navigate('/login')
    })
  }, [navigate])
  
  const loadPatients = async (clinicId) => {
    try {
      setLoading(true)
      setError('')
      const [pending, claimed] = await Promise.all([
        clinicAPI.listPendingPatients(clinicId),
        clinicAPI.listClaimedPatients(clinicId),
      ])
      setPendingPatients(pending.pending_patients || [])
      setClaimedPatients(claimed.claimed_patients || [])
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Błąd ładowania danych. Spróbuj ponownie.'
      setError(errorMsg)
      console.error('Error loading patients:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleClaim = async (submissionId) => {
    if (!clinicId) {
      setError('Brak wybranej przychodni.')
      return
    }
    
    try {
      setError('')
      await clinicAPI.claimPatient(submissionId, clinicId)
      // Reload patients
      await loadPatients(clinicId)
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Błąd claimowania pacjenta.'
      setError(errorMsg)
      console.error('Error claiming patient:', err)
    }
  }
  
  const handleViewPatient = (submissionId) => {
    navigate(`/patient/${submissionId}`)
  }
  
  const handleLogout = async () => {
    await authAPI.logout()
    navigate('/login')
  }
  
  if (loading) {
    return <div className="dashboard-page">Ładowanie...</div>
  }
  
  if (error && !clinicId) {
    return <div className="dashboard-page"><div className="error-message">{error}</div></div>
  }
  
  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="logo">WELLYSA</div>
        <div className="header-actions">
          <span className="clinic-name">Przychodnia #{clinicId}</span>
          <button onClick={handleLogout} className="btn-secondary">Wyloguj</button>
        </div>
      </header>
      
      <main className="dashboard-main">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Oczekujące ({pendingPatients.length})
          </button>
          <button
            className={`tab ${activeTab === 'claimed' ? 'active' : ''}`}
            onClick={() => setActiveTab('claimed')}
          >
            Zajęte ({claimedPatients.length})
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="patients-list">
          {activeTab === 'pending' && (
            <>
              {pendingPatients.length === 0 ? (
                <p className="empty-state">Brak oczekujących pacjentów</p>
              ) : (
                pendingPatients.map(patient => (
                  <div key={patient.id} className="patient-card">
                    <div className="patient-info">
                      <h3>
                        {patient.personal_info?.name} {patient.personal_info?.surname}
                      </h3>
                      <p>ID: {patient.id}</p>
                      <p>Data utworzenia: {new Date(patient.created_at * 1000).toLocaleDateString('pl-PL')}</p>
                    </div>
                    <div className="patient-actions">
                      <button
                        onClick={() => handleClaim(patient.id)}
                        className="btn-primary"
                      >
                        Zajmij pacjenta
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
          
          {activeTab === 'claimed' && (
            <>
              {claimedPatients.length === 0 ? (
                <p className="empty-state">Brak zajętych pacjentów</p>
              ) : (
                claimedPatients.map(patient => (
                  <div key={patient.id} className="patient-card">
                    <div className="patient-info">
                      <h3>
                        {patient.personal_info?.name} {patient.personal_info?.surname}
                      </h3>
                      <p>ID: {patient.id}</p>
                      <p>Zajęty: {new Date(patient.claimed_at * 1000).toLocaleDateString('pl-PL')}</p>
                    </div>
                    <div className="patient-actions">
                      <button
                        onClick={() => handleViewPatient(patient.id)}
                        className="btn-primary"
                      >
                        Zobacz szczegóły
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}

export default DashboardPage
